import {
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import type { Cell } from "@/lib/cells/cells";
import { cellQueryPolicy } from "@/lib/cells/cell-session";
import { mergeCellTenantCounts } from "@/lib/cells/cell-insights";
import { managementTenantId } from "@/lib/cells/management-tenant";
import {
  client as insightsClient,
  listRecommendationsOptions,
  getRecommendationOptions,
  getConversationEnrichmentOptions,
  listAnalysisRunsOptions,
  triggerAnalysisRun,
  updateRecommendation,
  bulkApproveRecommendations,
} from "@harmonyso/insights-engine-sdk";
import type {
  RecommendationResponse,
  RunResponse,
} from "@harmonyso/insights-engine-sdk";
import {
  listConversations,
  getConversation,
} from "@harmonyso/conversations-viewer-sdk";

export interface ThreadMessage {
  id: string;
  thread_id: string;
  tenant_id: string;
  role: "employee" | "system" | "agent";
  employee_id: string | null;
  user_source: string | null;
  system_source: string | null;
  system_actor: string | null;
  content: string | null;
  is_internal: boolean;
  timestamp: string;
}

interface ThreadMessagesResponse {
  items: ThreadMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface ArticleContent {
  title: string;
  representative_intent: string;
  insight: string;
  symptoms: string[];
  prerequisites: string[];
  resolution_steps: string[];
  key_insights: string[];
  edge_cases: string[];
  self_serviceable: boolean;
  self_serviceable_reason: string;
}

export interface CatalogContent {
  name: string;
  description: string;
  ticket_type: string;
  priority: string;
  representative_intent: string;
  form_fields: {
    label: string;
    type: string;
    required: boolean;
    options: string[];
    placeholder: string;
  }[];
}

export interface TicketMessage {
  role: string;
  content: string;
  employee_name: string;
  is_internal: boolean;
  created_at: string;
}

export interface TicketDetail {
  external_id: string;
  subject: string;
  category: string;
  sub_category: string;
  user_intent: string;
  entities: string;
  resolution_insights: string;
  ticket_type: string;
  status: string;
  messages: TicketMessage[];
}

export interface ConversationEnrichment {
  conversation_id: string;
  category: string;
  sub_category: string;
  user_intent: string;
  entities: string;
  resolution_insights: string;
}

export interface Evidence {
  supporting_conversation_ids: string[];
  conversation_count: number;
  resolution_consensus: string;
  outlier_resolutions: string[];
  common_symptoms: string[];
}

export type Recommendation = Omit<
  RecommendationResponse,
  "content" | "evidence" | "self_serviceable"
> & {
  content: ArticleContent;
  evidence: Evidence;
  self_serviceable: string | boolean | null;
  conversation_count?: number | null;
  evaluation?: Record<string, unknown> | null;
};

export type AnalysisRun = RunResponse;

export interface RecommendationsQuery {
  tenant_id?: string;
  status?: string[];
  type?: string[];
  category?: string[];
  sub_category?: string[];
  self_serviceable?: boolean | string[];
  kind?: string[];
  high_quality?: boolean;
  source?: string;
  search?: string;
  min_conversations?: number;
  max_conversations?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export function useRecommendations(
  query: RecommendationsQuery & { enabled?: boolean },
) {
  const { enabled = true, ...restQuery } = query;
  return useQuery({
    ...listRecommendationsOptions({
      query: restQuery as Record<string, unknown>,
    }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export function useRecommendation(id: string | null, tenantId?: string) {
  return useQuery({
    ...getRecommendationOptions({
      path: { rec_id: id ?? "" },
      // @ts-expect-error tenant_id is required by the proxy but not in the SDK types
      query: tenantId ? { tenant_id: tenantId } : undefined,
    }),
    enabled: !!id,
  });
}

export function useUpdateRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      dismiss_reason,
      content,
    }: {
      id: string;
      status?: string;
      dismiss_reason?: string;
      content?: Record<string, unknown>;
    }) => {
      const result = await updateRecommendation({
        path: { rec_id: id },
        body: { status, dismiss_reason, content },
      });
      return result.data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ["listRecommendations"] })
        .catch(() => undefined);
      queryClient
        .invalidateQueries({ queryKey: ["getRecommendation"] })
        .catch(() => undefined);
    },
  });
}

export function useBulkApprove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const result = await bulkApproveRecommendations({
        body: { ids },
      });
      return result.data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ["listRecommendations"] })
        .catch(() => undefined);
    },
  });
}

export function useTriggerRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      dataSource = "conversations",
    }: {
      tenantId: string;
      dataSource?: string;
    }) => {
      const result = await triggerAnalysisRun({
        body: {
          tenant_id: tenantId,
          ...({ data_source: dataSource } as Record<string, unknown>),
        },
      });
      return result.data;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ["listAnalysisRuns"] })
        .catch(() => undefined);
    },
  });
}

export function useRuns(tenantId: string | null) {
  return useQuery({
    ...listAnalysisRunsOptions({
      query: { tenant_id: tenantId ?? "" },
    }),
    enabled: !!tenantId,
    refetchInterval: 5000,
  });
}

export function useConversationBySourceId(
  sourceId: string | null,
  tenantId: string,
) {
  return useQuery({
    queryKey: ["conversationBySource", sourceId, tenantId],
    queryFn: async () => {
      const threadId = `thread:${sourceId}`;
      const { data } = await listConversations({
        query: {
          source_identifier: threadId,
          tenant_id: tenantId,
          page_size: 1,
        },
        throwOnError: true,
      });
      const viewerId = data.conversations[0]?.id;
      if (!viewerId) return null;
      const conv = await getConversation({
        path: { conversation_id: viewerId },
        query: { tenant_id: tenantId },
        throwOnError: true,
      });
      return conv.data;
    },
    enabled: !!sourceId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetches thread messages (service desk follow-ups + internal
 * notes) for a given sourceId. The sourceId in kb-insights
 * recommendations is the thread UUID used by the threads-api.
 */
export function useThreadMessages(
  sourceId: string | null,
  tenantId: string | null,
) {
  return useQuery<ThreadMessage[]>({
    queryKey: ["threadMessages", sourceId, tenantId],
    queryFn: async () => {
      const resp = await insightsClient.get<ThreadMessagesResponse>({
        url: `/api/v1/internal/threads/${sourceId}/messages`,
        query: {
          tenant_id: tenantId,
          include_internal: true,
          limit: 200,
          offset: 0,
        },
      });
      return resp.data?.items ?? [];
    },
    enabled: !!sourceId && !!tenantId,
    staleTime: 5 * 60_000,
  });
}

export function useEnrichment(
  conversationId: string | null,
  tenantId?: string,
) {
  return useQuery({
    ...getConversationEnrichmentOptions({
      path: { conversation_id: conversationId ?? "" },
      // The proxy requires a tenant; it also sends the call to that tenant's cell.
      ...(tenantId && { query: { tenant_id: tenantId } as never }),
    }),
    enabled: !!conversationId,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });
}

/**
 * A ticket from the insights pipeline. The endpoint is management-only, so
 * it's asked with the management tenant of `cell`, the cell the ticket's
 * tenant lives in, which also sends the call there.
 */
export function useTicketDetail(ticketId: string | null, cell?: Cell) {
  const managementTenant = cell ? managementTenantId(cell) : undefined;
  return useQuery<TicketDetail>({
    queryKey: ["ticketDetail", ticketId, managementTenant],
    queryFn: async () => {
      const resp = await insightsClient.get<TicketDetail>({
        url: `/api/v1/internal/tickets/${ticketId}`,
        query: { tenant_id: managementTenant },
      });
      if (!resp.data) throw new Error("No data");
      return resp.data;
    },
    enabled: !!ticketId && !!managementTenant,
    staleTime: 30 * 60_000,
  });
}

interface TenantCountItem {
  tenant_id: string;
  count: number;
}

/**
 * Recommendation counts per tenant from each of `cells`, asked with each
 * cell's management tenant. A tenant is counted only from its own cell (see
 * mergeCellTenantCounts), using `tenants` to know where each one lives.
 */
export function useCellRecommendationCountsByTenant(
  cells: Cell[],
  tenants: { id: string; deployment_cell?: string | null }[] | undefined,
) {
  const homeCells = new Map(
    (tenants ?? []).map((tenant) => [
      tenant.id,
      tenant.deployment_cell ?? undefined,
    ]),
  );
  return useQueries({
    queries: cells.map((cell) => {
      const tenantId = managementTenantId(cell);
      return {
        queryKey: ["recommendationCountsByTenant", cell.id, tenantId],
        queryFn: async () => {
          const resp = await insightsClient.get({
            url: "/api/v1/internal/management/recommendations/counts-by-tenant",
            query: { tenant_id: tenantId },
            throwOnError: true,
          });
          const data = resp.data as { items: TenantCountItem[] } | undefined;
          return data?.items ?? [];
        },
        staleTime: 2 * 60_000,
        gcTime: 5 * 60_000,
        ...cellQueryPolicy(cell),
      };
    }),
    combine: (results) => {
      const merged = mergeCellTenantCounts(
        cells.map((cell, index) => ({
          cell,
          items: results.at(index)?.data,
          error: results.at(index)?.data ? null : results.at(index)?.error,
          isLoading: results.at(index)?.isPending,
        })),
        (tenantId) => homeCells.get(tenantId),
      );
      return { ...merged, isLoading: merged.loadingCells.length > 0 };
    },
  });
}
